import React, { memo } from "react";

import useI18n from "Hooks/UseI18n";
import classNames from "classnames";
import { Checkbox, RadioButton, Tooltip } from "@toddle-edu/ds-web";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import { useDispatch } from "Hooks/useDispatch";

import { createPollResponse } from "MessagingV2/modules/MessagingModule";
import { unifiedToNative } from "MessagingV2/modules/Utils";
import _ from "lodash";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import classes from "./PollGraph.scss";

const POLL_TEXT_LENGTH = 40;

const Poll = props => {
  const {
    totalCount,
    poll,
    canEditPoll,
    canSeeResult,
    isMultipleVoteAllowed,
    messageId,
  } = props;

  const unified = _.get(poll, "metadata.unified");
  const voteCount = _.get(poll, "selectedByUsersCount", 0);
  const { t } = useI18n(["common"]);

  const dispatch = useDispatch();

  const onClick = () => {
    if (!canEditPoll) return;
    dispatch(createPollResponse({ messageId, optionId: poll.id }));
  };

  const percentage = canSeeResult
    ? _.round(totalCount ? (voteCount / totalCount) * 100 : 0, 2)
    : poll.isSelectedByUser
    ? 100
    : 0;

  const onKeyPressHandle = e => {
    onEnterOrSpaceKeyDown(e, onClick);
  };

  return (
    <div
      className={classNames(classes.pollContainerWrapper, {
        [classes.pollContainerWrapperWithUserVote]: poll.isSelectedByUser,
      })}
      onClick={onClick}
      role={canEditPoll ? "button" : undefined}
      tabIndex={0}
      onKeyDown={onKeyPressHandle}
      aria-label={poll.label}
    >
      <div
        className={classNames(classes.pollContainerBackground, {
          [classes.pollContainerBackgroundColorWithUserVote]:
            poll.isSelectedByUser,
        })}
        style={{ width: `${percentage}%` }}
      />
      <div className={classes.pollContainer}>
        <div className={classes.pollLeftContainer}>
          {unified ? (
            <div className={classes.pollEmoji}>{unifiedToNative(unified)}</div>
          ) : null}

          <ConditionalWrapper
            wrapper={<Tooltip tooltip={poll.label} />}
            condition={_.size(poll.label) > POLL_TEXT_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className="text-body">
              {_.truncate(poll.label, { length: POLL_TEXT_LENGTH })}
            </div>
          </ConditionalWrapper>
        </div>
        <div className={classes.pollRightContainer}>
          {canSeeResult ? (
            <>
              <div className={classNames("text-body-s", classes.pollVoteCount)}>
                {t("common:count_vote", {
                  count: voteCount,
                })}
              </div>
              <div className="text-body">{percentage}%</div>
            </>
          ) : null}
          {canEditPoll ? (
            isMultipleVoteAllowed ? (
              <Checkbox
                dsVersion="2.0"
                isChecked={poll.isSelectedByUser}
                className={classes.pollCheckbox}
              />
            ) : (
              <RadioButton
                dsVersion="2.0"
                checked={poll.isSelectedByUser}
                className={classes.pollCheckbox}
              />
            )
          ) : null}
        </div>
      </div>
    </div>
  );
};

const PollGraph = props => {
  const {
    interaction,
    overrideClasses = {},
    isEditable = true,
    messageId,
    isCreator,
  } = props;

  const options = _.get(interaction, "options", []);
  const isResponseEditAllowed = _.get(
    interaction,
    "isResponseEditAllowed",
    false
  );
  const isMultipleVoteAllowed = _.get(
    interaction,
    "isMultipleVoteAllowed",
    false
  );

  const isResultPublic = _.get(interaction, "isResultPublic", false);

  const isResponseActive = _.get(interaction, "isResponseActive", false);

  const isResponseAdded = _.some(options, option =>
    _.get(option, "isSelectedByUser", false)
  );

  const totalCount = _.reduce(
    options,
    (sum, pollOption) => sum + _.get(pollOption, "selectedByUsersCount", 0),
    0
  );

  const canSeeResult = isResultPublic || isCreator;

  return (
    <div
      className={classNames(
        classes.pollGraphContainer,
        overrideClasses.pollGraphContainer
      )}
    >
      {_.map(options, pollOption => {
        const id = _.get(pollOption, "id");

        const isSelectedByUser = _.get(pollOption, "isSelectedByUser", false);

        const canEditPoll =
          isResponseActive &&
          isEditable &&
          ((isMultipleVoteAllowed && !isSelectedByUser) ||
            isResponseEditAllowed ||
            !isResponseAdded);

        return (
          <Poll
            key={id}
            messageId={messageId}
            totalCount={totalCount}
            poll={pollOption}
            overrideClasses={overrideClasses}
            canEditPoll={canEditPoll}
            canSeeResult={canSeeResult}
            isMultipleVoteAllowed={isMultipleVoteAllowed}
          />
        );
      })}
    </div>
  );
};

export default memo(PollGraph);
